import { apiClient } from '@libs/api/client';
import type { ApiResponse } from '@libs/api/types';
import type { ExternalPoint, ExternalPointCreateRequest, ExternalPointUpdateRequest } from './types';

const ADMIN_ENDPOINT = '/admin/external-points';
const PUBLIC_ENDPOINT = '/external-points';

const buildFormData = (
  request: ExternalPointCreateRequest | ExternalPointUpdateRequest,
  imageFile?: File | null,
): FormData => {
  const formData = new FormData();
  const requestBlob = new Blob([JSON.stringify(request)], { type: 'application/json' });
  formData.append('request', requestBlob);
  if (imageFile) {
    formData.append('image', imageFile);
  }
  return formData;
};

export const externalPointService = {
  getAll: async (): Promise<ApiResponse<ExternalPoint[]>> => {
    const response = await apiClient.get<ApiResponse<ExternalPoint[]>>(ADMIN_ENDPOINT);
    return response.data;
  },

  getById: async (id: number): Promise<ApiResponse<ExternalPoint>> => {
    const response = await apiClient.get<ApiResponse<ExternalPoint>>(`${ADMIN_ENDPOINT}/${id}`);
    return response.data;
  },

  getActiveList: async (): Promise<ApiResponse<ExternalPoint[]>> => {
    const response = await apiClient.get<ApiResponse<ExternalPoint[]>>(`${PUBLIC_ENDPOINT}/active-list`);
    return response.data;
  },

  getByCode: async (code: string): Promise<ApiResponse<ExternalPoint>> => {
    const response = await apiClient.get<ApiResponse<ExternalPoint>>(`${PUBLIC_ENDPOINT}/by-code`, {
      params: { code },
    });
    return response.data;
  },

  create: async (request: ExternalPointCreateRequest, imageFile?: File | null): Promise<ApiResponse<ExternalPoint>> => {
    const formData = buildFormData(request, imageFile);
    const response = await apiClient.post<ApiResponse<ExternalPoint>>(ADMIN_ENDPOINT, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  update: async (
    id: number,
    request: ExternalPointUpdateRequest,
    imageFile?: File | null,
  ): Promise<ApiResponse<ExternalPoint>> => {
    const formData = buildFormData(request, imageFile);
    const response = await apiClient.put<ApiResponse<ExternalPoint>>(`${ADMIN_ENDPOINT}/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  delete: async (id: number): Promise<ApiResponse<null>> => {
    const response = await apiClient.delete<ApiResponse<null>>(`${ADMIN_ENDPOINT}/${id}`);
    return response.data;
  },
};
